import React, {useState} from "react";
import OpenHour from "./OpenHour";
import { TextG12, TextG14 } from "../typography";
import {
  Accordion as ChkrAccordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  Flex,
  Grid,
  GridItem,
  Box, Switch, Input, Button,
} from "@chakra-ui/react";
import { PLACE_STATUS } from "../../constants/openingHours";
import {capitalize} from 'lodash';
import {
  BusinessHours,
  DAY_ORDER,
  getEnd,
  getStart,
  isValidRange, normalizeBusinessHours, orderBusinessHours,
  Weekday
} from '../../utils/openingHours';
import {Place} from "../../types/article";
import useOpenHours from "../../hooks/useOpenHours";
import Icon from "../Icon";
import {updatePlace} from "../../endpoints/api";
import {EntityRole, useGetCurrentUserRoleForEntity} from "../../hooks/useEntityUserRole";

export const getAvailabilityStatus = (businessStatus?: string, openStatus?: string) => {
  if (businessStatus === 'closed_temporarily') {
    return 'Temporarily Closed';
  }

  if(!openStatus || openStatus === PLACE_STATUS.UNKNOWN) {
    return 'Business Hours Unknown';
  }

  const capitalizedStatus = capitalize(openStatus).replace('_', ' ');

  if (openStatus === PLACE_STATUS.OPEN || openStatus === PLACE_STATUS.CLOSED) {
    return `${capitalizedStatus} Now`;
  }
  return capitalizedStatus;
};

export const getAvailabilityStatusColor = (businessStatus?: string, openStatus?: string) => {
  if (businessStatus === 'closed_temporarily') {
    return '#F9AC39';
  }

  if (openStatus === PLACE_STATUS.OPEN || openStatus === PLACE_STATUS.OPENING_SOON) {
    return '#329241';
  }

  return 'red';
};

export function OpenHours({ place, refetchPlace }: { place: Place, refetchPlace: () => void}) {
  const {placeOpenHours} = useOpenHours(place?.business_hours?.weekday_text);

  const initialHours = normalizeBusinessHours(
    place.business_hours?.periods ?? {}
  );

  const [draftHours, setDraftHours] =
    useState<BusinessHours>(initialHours);
  const [errors, setErrors] =
    useState<Record<Weekday, string | null>>({} as any);
  const [editing, setEditing] = useState(false);

  const { data: currentRole } = useGetCurrentUserRoleForEntity('places', place.id!);
  const canEdit = currentRole?.data === EntityRole.MANAGER || currentRole?.data === EntityRole.TEAM_MEMBER;

  function toggleDay(day: Weekday, open: boolean) {
    setDraftHours((p) => ({
      ...p,
      [day]: open ? ['08:00-20:00'] : [],
    }));
    setErrors((e) => ({ ...e, [day]: null }));
  }

  function updatePeriod(
    day: Weekday,
    value: string,
    type: 'start' | 'end'
  ) {
    setDraftHours((p) => {
      const [s, e] = p[day][0].split('-');
      const ns = type === 'start' ? value : s;
      const ne = type === 'end' ? value : e;

      setErrors((err) => ({
        ...err,
        [day]: isValidRange(ns, ne)
          ? null
          : 'End must be later than start',
      }));

      return { ...p, [day]: [`${ns}-${ne}`] };
    });
  }

  async function save() {
    if (Object.values(errors).some(Boolean)) return;

    await updatePlace(place.id, {
      business_hours: orderBusinessHours(draftHours),
    });

    await refetchPlace();

    setEditing(false);
  }

  const openHoursList = placeOpenHours.map((openHour) => (
    <OpenHour
      key={openHour.day}
      day={openHour.day}
      hours={openHour.hours}
      current={openHour.current}
    />
  ));  

  return (
    <ChkrAccordion allowToggle defaultIndex={[0]}>
      <AccordionItem>
      <AccordionButton position="relative" paddingLeft={0} paddingRight={"8px"}>
          <Flex alignItems="center" h="100%" w="100%" position={"relative"}>
            <Box width={"17%"} textAlign="left"><TextG12 fontWeight="bold">Hours: </TextG12></Box>
            <Box width={"66%"} textAlign="left"><TextG12
              fontWeight={place?.open_status === PLACE_STATUS.OPEN && place?.business_status && place?.business_status === 'operational' && "bold"}
              color={getAvailabilityStatusColor(place?.business_status, place?.open_status)}
            >{getAvailabilityStatus(place?.business_status, place?.open_status)} </TextG12></Box>
            <Box width={"17%"} textAlign="right"><AccordionIcon /></Box>
          </Flex>
        </AccordionButton>
        <AccordionPanel paddingStart={0} paddingRight={0}>
          {canEdit && (
            <Icon iconName="fi fi-rr-edit" style={{
              color: '#2c5aa0',
              fontSize: '16px',
            }} onClick={() => setEditing(true)}
            />
          )}
          {!editing ? (<GridItem width={"100%"} margin="auto">{openHoursList}</GridItem>) : (
            <Box>
              {DAY_ORDER.map(({ key, label }) => {
                const open = draftHours[key]?.length > 0;

                return (
                  <Flex
                    key={key}
                    direction={{ base: 'column', md: 'row' }}
                    gap="8px"
                    py="6px"
                  >
                  <Flex align="center" justify="space-between">
                    <TextG14 fontWeight="500">
                      {label}
                    </TextG14>

                    <Switch
                      colorScheme="green"
                      isChecked={open}
                      onChange={(e) => toggleDay(key, e.target.checked)}
                    />
                  </Flex>
                {open ? (
                  <Flex align="center" gap="8px">
                    <Input
                      type="time"
                      value={getStart(draftHours[key][0])}
                      onChange={(e) =>
                        updatePeriod(key, e.target.value, 'start')
                      }
                      flex="1"
                    />
                    <TextG14>–</TextG14>
                    <Input
                      type="time"
                      value={getEnd(draftHours[key][0])}
                      onChange={(e) =>
                        updatePeriod(key, e.target.value, 'end')
                      }
                      flex="1"
                    />
                  </Flex>
                ) : (
                  <TextG14 color="gray.400" fontSize="sm">
                    Closed
                  </TextG14>
                )}

                    {errors[key] && (
                      <TextG14 fontSize="sm" color="red">
                        {errors[key]}
                      </TextG14>
                    )}
                  </Flex>
                );
              })}

              <Flex mt="12px" gap="8px">
                <Button size="sm" onClick={save}>
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditing(false)}
                >
                  Cancel
                </Button>
              </Flex>
            </Box>
          )}
        </AccordionPanel>
      </AccordionItem>
    </ChkrAccordion>
  );
}
